import { DbRef, UNKNOWN_SCHEDULING_USER, Role, MedicalTitleName } from '@caresend/types';
import { getPath } from './path.js';
import { getMaxInArray } from '../languageExtensions/array.js';
import { isNullish } from '../typeValidation/nullish.js';
import '../typeValidation/booking.js';
import '../typeValidation/draftWaypoint.js';
import { isInEnum } from '../typeValidation/enum.js';
import '../typeValidation/feedback.js';
import '../environment/crypto/index.js';
import 'dayjs';
import '../formatters/string.js';
import 'ngeohash';
import '../formatters/location.js';
import 'immutability-helper';
import '../initializers/user.js';
import '../typeValidation/note.js';
import '../typeValidation/procedure.js';
import '../typeValidation/samples.js';
import '../typeValidation/shift.js';
import '../typeValidation/shipmentInfo.js';
import '../typeValidation/supply.js';
import '../typeValidation/timeSlot.js';
import '../typeValidation/waypoint.js';
import '../typeValidation/waypointAction.js';

/**
 * ```
 * `${DbRef.USERS}/${id}/${childPath}`
 * ```
 */

function getUserPath(id, childPath) {
  return getPath(`${DbRef.USERS}/${id}`, childPath);
}
const shouldEncryptSchedulingUserName = schedulingUserName => schedulingUserName.toLowerCase() !== UNKNOWN_SCHEDULING_USER.toLowerCase();
var AddPatientEntityType;

(function (AddPatientEntityType) {
  AddPatientEntityType["PATIENT"] = "patient";
  AddPatientEntityType["CONTACT"] = "contact";
})(AddPatientEntityType || (AddPatientEntityType = {}));
/**
 * Get filters for patient or scheduling contact matching
 *
 * Throws if missing required fields:
 * - DOB and gender required for patients
 * - email or phone required for contacts
 */


const getFacetFiltersForPatientMatching = (data, entityType) => {
  const {
    firstName,
    lastName,
    dateOfBirth,
    gender,
    email,
    phone
  } = data;
  const roleFilter = `role:${Role.PATIENT}`;
  const firstNameFilter = `info.firstName:${firstName}`;
  const lastNameFilter = `info.lastName:${lastName}`;
  const dobFilter = !isNullish(dateOfBirth) ? `info.dateOfBirth:${dateOfBirth}` : null;
  const genderFilter = gender ? `info.gender:${gender}` : null;
  const emailFilter = email ? `info.email:${email}` : null;
  const phoneFilters = phone ? [`info.phone.number:${phone === null || phone === void 0 ? void 0 : phone.number}`, `info.phone.callingCode:${phone === null || phone === void 0 ? void 0 : phone.callingCode}`] : null;
  const facetFilters = [roleFilter];

  if (entityType === AddPatientEntityType.PATIENT) {
    if (!dobFilter) throw Error('DOB is required for patient matching');
    if (!genderFilter) throw Error('Gender is required for patient matching');
    facetFilters.push(firstNameFilter, lastNameFilter, dobFilter, genderFilter);
  } else {
    if (!emailFilter && !phoneFilters) {
      throw Error('Email or phone is required for contact matching');
    }

    if (emailFilter) {
      facetFilters.push(emailFilter);
    } else {
      facetFilters.push(firstNameFilter, lastNameFilter);
    }

    if (phoneFilters) facetFilters.push(...phoneFilters);
  }

  return facetFilters;
};
const getValidatedMedicalTitle = medicalTitle => {
  const lowerCaseMedicalTitle = medicalTitle.toLowerCase();
  return isInEnum(lowerCaseMedicalTitle, MedicalTitleName) ? lowerCaseMedicalTitle : undefined;
};
/**
 * Returns the latest medical license number from an array of medical licenses
 * on a user's professional information.
 */

const getLatestMedicalLicenseNumberOnUser = user => {
  var _a, _b, _c;

  const medicalLicenses = (_b = (_a = user.professionalInfo) === null || _a === void 0 ? void 0 : _a.licences) !== null && _b !== void 0 ? _b : [];
  return (_c = getMaxInArray(medicalLicenses, 'expiryDate')) === null || _c === void 0 ? void 0 : _c.number;
};
const getPrescriberInfoFromStaffUser = user => {
  var _a;

  const {
    info,
    professionalInfo
  } = user;
  if (!professionalInfo) throw new Error('Missing professional info');
  const {
    npi,
    title
  } = professionalInfo;
  if (!title) throw new Error('Missing medical title');
  const medicalTitle = getValidatedMedicalTitle(title);
  if (!medicalTitle) throw new Error(`Invalid medical title ${title}`);
  if (!npi) throw new Error('Missing NPI');
  const medicalLicenseNumber = getLatestMedicalLicenseNumberOnUser(user);
  if (!medicalLicenseNumber) throw new Error('Missing medical license number');
  return {
    firstName: info.firstName,
    lastName: info.lastName,
    middleInitial: (_a = info.middleNames) === null || _a === void 0 ? void 0 : _a[0],
    medicalTitle,
    medicalLicenseNumber,
    npi
  };
};

export { AddPatientEntityType, getFacetFiltersForPatientMatching, getLatestMedicalLicenseNumberOnUser, getPrescriberInfoFromStaffUser, getUserPath, getValidatedMedicalTitle, shouldEncryptSchedulingUserName };
